// Who sells a membership to a buyer, decided when a checkout starts
// (docs/accounts.md, "Sales tax, VAT and GST"). The checkout route, the
// event it records and the membership dialog all read it from here; it
// imports nothing, so the game hosts' Worker carries no accounts code for it.

/**
 * Who sells a membership. "link": Stripe's merchant of record, under the
 * name Link, which handles the sale's sales tax, VAT and GST (Managed
 * Payments). "direct": Pocket Nexus, with Stripe taking the payment.
 */
export type Seller = "link" | "direct";

/**
 * The IANA time zones of mainland China, with their older names. Hong Kong,
 * Macau and Taiwan have zones of their own.
 */
const MAINLAND_ZONES = new Set(["Asia/Shanghai", "Asia/Urumqi", "Asia/Chongqing", "Asia/Chungking", "Asia/Harbin", "Asia/Kashgar", "PRC"]);

/** Whether a browser's time zone is one of mainland China's. */
export const mainlandZone = (timeZone: string | null | undefined): boolean => !!timeZone && MAINLAND_ZONES.has(timeZone);

/** What a checkout request says about where its buyer is. */
export interface BuyerPlace {
  /** The country Cloudflare reports for the request's network address. */
  country?: string | null;
  /** The browser's time zone, as `Intl.DateTimeFormat().resolvedOptions().timeZone` answers it. */
  timeZone?: string | null;
  /** The buyer said so: "Pay from mainland China" on the membership dialog. */
  mainland?: boolean;
}

/**
 * Who sells to this buyer when Managed Payments is switched on. Link does
 * not sell to a buyer whose country is China: its checkout answers "China is
 * not currently supported." So a buyer in mainland China gets the direct
 * sale. Any one sign is enough, because a wrong guess toward "direct" still
 * ends in a checkout that works, and a wrong guess toward "link" ends in one
 * that refuses the buyer's own country:
 *
 *   - the request comes from a network address in mainland China ("CN";
 *     Hong Kong, Macau and Taiwan have codes of their own);
 *   - the browser's time zone is mainland China's, which a buyer behind a
 *     proxy in another country still has;
 *   - the buyer said so.
 */
export function sellerFor(place: BuyerPlace): Seller {
  return place.mainland === true || place.country?.toUpperCase() === "CN" || mainlandZone(place.timeZone) ? "direct" : "link";
}
